import { Injectable, signal, computed, inject } from '@angular/core'
import { CardMapper } from './utils/card-mapper'
import { KanbanColumn } from './utils/kanban-column'
import {
  GetCardsByBoardResponse,
  GetCardsByPhaseResponse,
  Kanban
} from '../../_services/kanban/kanban.model'
import { PvtKanbanService } from '../../_services/kanban/kanban.service'
import { Observable, of, switchMap, tap } from 'rxjs'
import { KanbanFiltersChange } from './widgets/kanban-filters/kanban-filters.model'

@Injectable()
export class KanbanService {
  private pvtKanbanService = inject(PvtKanbanService)
  private _board = signal<Kanban.Board.Entity | null>(null)
  private _columns = signal<KanbanColumn[] | null>(null)

  board = this._board.asReadonly()
  columns = this._columns.asReadonly()
  tags = computed(() => this.board()?.tags || [])
  phases = computed(() => this.board()?.phases ?? [])
  boardTitle = computed(() => this.board()?.name)
  fields = computed(() => this.board()?.fields || [])

  selectedFilters = signal<KanbanFiltersChange>([])
  cardFilters = computed<Kanban.Card.Filters.Filter[]>(() =>
    this.selectedFilters().map(
      filter =>
        ({
          fixed: filter.field.fixed,
          id: filter.field.id,
          value: filter.value,
          operator: filter.operator
        }) as Kanban.Card.Filters.Filter
    )
  )

  getKanban(boardId: string) {
    return this.pvtKanbanService.getBoard(boardId).pipe(
      tap(board => {
        this._board.set(board)
        this._columns.set(board.phases.map(phase => new KanbanColumn(phase.id, [], 0, false)))
      }),
      switchMap(() => this.pvtKanbanService.getCards(boardId, this.cardFilters()))
    )
  }

  initBoard(boardResponse: GetCardsByBoardResponse) {
    const columns = this._board()!.phases.map(phase => {
      const dbCol = boardResponse.cards.find(c => c.phaseId === phase.id)
      const mappedCards = (dbCol?.cards ?? []).map(card => this.mapCardFields(card))
      return new KanbanColumn(phase.id, mappedCards, dbCol?.totalCount ?? 0, dbCol !== undefined)
    })
    this._columns.set(columns)
  }

  getCardsByPhase(phaseId: string) {
    const column = this._columns()!.find(c => c.phaseId === phaseId)
    if (!column?.hasMore) return of()

    return this.pvtKanbanService.getCardsByPhase(
      this._board()!.id,
      phaseId,
      column.offset,
      this.cardFilters()
    )
  }

  getPhaseName(phaseId: string): string {
    const phase = this.board()?.phases.find(phase => phase.id === phaseId)
    return phase ? phase.name : ''
  }

  updatePhase(phaseResponse: GetCardsByPhaseResponse) {
    this._columns.update(columns => {
      if (!columns) return columns
      return columns.map(column => {
        if (column.phaseId !== phaseResponse.phaseId) return column
        const mappedCards = phaseResponse.cards.map(card => this.mapCardFields(card))
        return column.appendCards(mappedCards, phaseResponse.pagination.hasMore)
      })
    })
  }

  updateCard(cardId: string): Observable<Kanban.Card.Entity> {
    return this.pvtKanbanService.getCardById(this._board()!.id, cardId).pipe(
      tap((updatedCard: Kanban.Card.Entity) => {
        this._columns.update(columns => {
          if (!columns) return columns
          const cardWithLabels = this.mapCardFields(updatedCard)
          return columns.map(column => {
            if (column.hasCard(updatedCard.id) && column.phaseId !== updatedCard.phaseId) {
              return column.removeCard(updatedCard.id)
            }
            if (column.hasCard(updatedCard.id) && column.phaseId === updatedCard.phaseId) {
              return column.replaceCard(cardWithLabels)
            }
            if (!column.hasCard(updatedCard.id) && column.phaseId === updatedCard.phaseId) {
              return column.prependCard(cardWithLabels)
            }
            return column
          })
        })
      })
    )
  }

  getCardHistory(cardId: string) {
    return this.pvtKanbanService.getCardHistory(this._board()!.id, cardId)
  }

  private mapCardFields(card: Kanban.Card.Entity) {
    return CardMapper.mapCardFields(card, this._board()!.fields)
  }
}
